import React from 'react';
import { connect } from 'react-redux';
import { withTheme } from 'styled-components';
import { KEYS } from 'constants/common';
import { Container } from './FlipCards.styled';
import { CARD_DIMENSION_MAP, TEXT_STOP_POINTS, ROW_SIZE_MAPPING } from './types';
import { localize } from 'core/localization';

type FlipCardProps = {
  content: string;
  theme: { [key: string]: any };
  inverted: boolean;
};

type Element = {
  template: string;
  props: {
    cardDimension: number;
    alignment: string;
    rowSize: number;
  };
};

type FlipCardState = {
  elements: Array<Element>;
};

const ATTRIBUTE_ARIA_HIDDEN = 'aria-hidden';

const getFrontSideList = (element: any) => [
  ...element.querySelectorAll('.column .flip-card-front-side')
];
const getBackSideList = (element: any) => [
  ...element.querySelectorAll('.column .flip-card-back-side')
];
const getParagraphList = (element: any) => [...element.querySelectorAll('p')];

const getStopPoint = (textLength: number) =>
  Object.values(TEXT_STOP_POINTS).find(POINT => textLength <= POINT);

const setStopPointClass = (element: any) =>
  [getFrontSideList, getBackSideList].map(getSideList =>
    getSideList(element).forEach((side: any) => {
      const textLength = getParagraphList(side)
        .map((p: any) => p.innerText.length)
        .reduce((a: number, c: number) => a + c, 0);
      getParagraphList(side).forEach((p: any) =>
        p.classList.add(`stop-point-${getStopPoint(textLength)}`)
      );
    })
  );

const getImageContainer = () => document.querySelectorAll('.cropped-image');

const setImageContainerHeight = () =>
  getImageContainer()?.forEach((img: any) => {
    img.style.height = '100%';
  });

const getColumnCount = (el: any) => el.children.length;

const getColumnSize = (element: any) => element.getAttribute('data-flex-active-class');

const getCardAlignment = (element: any) => element.getAttribute('data-flex-alignment-active-class');

const getFrontFace = (element: any) => element.querySelectorAll('.flip-card-front-side')[0];

const getBackFace = (element: any) => element.querySelectorAll('.flip-card-back-side')[0];

const isFlipped = (element: any) => element.parentElement.classList.contains('flip');

const getFlipCardWrappers = () =>
  document.querySelectorAll('[data-type="flipCards"] > .column > .row');

const addFlipButtons = () =>
  getFlipCardWrappers()?.forEach(el => {
    if (!el.querySelectorAll('.flip-button').length) {
      const button = document.createElement('div');
      button.innerHTML = `
        <span class="icon icon-flip" style="font-size: 12px;"></span>
        <span>
        ${localize(`[click to flip]`)}
        </span>
      `;
      button.classList.add('flip-button');
      el.appendChild(button);
    }
  });

const getFlipCardUpdate = () => document.getElementById('flipCardUpdate');

const flipElement = (element: any) => {
  element.parentElement.classList.toggle('flip');
};

const updateAriaHidden = (frontFace: any, backFace: any, activeFace: any) => {
  if (frontFace === activeFace) {
    frontFace.setAttribute(ATTRIBUTE_ARIA_HIDDEN, 'false');
    backFace.setAttribute(ATTRIBUTE_ARIA_HIDDEN, 'true');
  } else {
    frontFace.setAttribute(ATTRIBUTE_ARIA_HIDDEN, 'true');
    backFace.setAttribute(ATTRIBUTE_ARIA_HIDDEN, 'false');
  }
};

const clickHandler = (event: any) => {
  if (event.type === 'keydown') {
    if (![KEYS.SPACEBAR, KEYS.ENTER].includes(event.key)) {
      return;
    }
    event.preventDefault();
  }

  const element = event.currentTarget.parentNode.querySelector('.flip-card-wrapper');
  flipElement(element);

  const frontFace = getFrontFace(element);
  const backFace = getBackFace(element);
  const activeFace = isFlipped(element) ? backFace : frontFace;
  updateAriaHidden(frontFace, backFace, activeFace);
  const imgAltText = activeFace.querySelector('img')?.alt || '';
  const screenReaderContent = `${activeFace.innerText} ${imgAltText}`;
  const flipCardUpdate = getFlipCardUpdate();
  if (flipCardUpdate) {
    flipCardUpdate.children[0].innerHTML = `Flipcard reads ${screenReaderContent}`;
  }
};

const attachClickHandlers = (card: any) => {
  const clickEvents = ['keydown', 'click'];
  clickEvents.forEach(eventType => {
    card.addEventListener(eventType, clickHandler);
  });
};

const addAriaHidden = () => {
  document
    .querySelectorAll('.flip-card-back-side')
    .forEach(bs => bs.setAttribute(ATTRIBUTE_ARIA_HIDDEN, 'true'));
};

const addAriaLive = () => {
  if (document.getElementById('flipCardUpdate')) {
    return;
  }
  const ariaDiv = document.createElement('div');
  const ariaP = document.createElement('p');
  ariaDiv.setAttribute('id', 'flipCardUpdate');
  ariaDiv.setAttribute('aria-live', 'assertive');
  ariaDiv.style.position = 'absolute';
  ariaDiv.style.top = '-1000px';
  ariaDiv.style.height = '0px';
  ariaP.style.height = '0px';
  ariaDiv.appendChild(ariaP);
  document.body.appendChild(ariaDiv);
};

const getCardDimension = (el: any) => CARD_DIMENSION_MAP[getColumnSize(el)];
export class FlipCards extends React.Component<FlipCardProps, FlipCardState> {
  constructor(props: FlipCardProps) {
    super(props);
    this.state = {
      elements: []
    };
  }

  getFlipCardsElement = () => {
    const el = document.createElement('div');
    el.innerHTML = this.props.content;
    return [...el.querySelectorAll('[data-type="flipCards"]')];
  };

  componentDidMount() {
    const elements: Array<Element> = [];
    addAriaLive();
    this.getFlipCardsElement().forEach(el => {
      const authorRowSize = ROW_SIZE_MAPPING[getColumnSize(el)];
      setStopPointClass(el);
      elements.push({
        template: el.outerHTML,
        props: {
          cardDimension: getCardDimension(el),
          alignment: getCardAlignment(el),
          rowSize: getColumnCount(el) > authorRowSize ? authorRowSize : getColumnCount(el)
        }
      });
    });
    this.setState({ elements });
  }

  componentDidUpdate() {
    addAriaHidden();
    setImageContainerHeight();
    addFlipButtons();
    const cards = document.querySelectorAll('.flip-card-wrapper');
    const flipButtons = document.querySelectorAll('.flip-button');
    for (const card of cards) {
      card.setAttribute('tabindex', '1');
      attachClickHandlers(card);
    }
    for (const button of flipButtons) {
      button.setAttribute('tabindex', '1');
      attachClickHandlers(button);
    }
  }

  render() {
    return (
      <>
        {this.state.elements.map((el, idx) => (
          <Container
            key={idx}
            {...el.props}
            dangerouslySetInnerHTML={{ __html: el.template }}
          ></Container>
        ))}
      </>
    );
  }
}

export default withTheme(connect()(FlipCards));
